<template>
  <div class="item-popup" itemscope itemtype="https://schema.org/Product">
    <div class="item-popup__main">
      <div class="item-popup__slider-wrap">
        <div ref="swiper" class="swiper item-popup__slider">
          <div class="swiper-wrapper">
            <template v-if="images.length">
              <div v-for="image in images" :key="image.id" class="swiper-slide item-popup-slider__slide">
                <a :href="resolveAssetUrl(image.image)" target="_blank" class="item-popup__slider-maximize">
                  <img src="@/assets/images/icons/maximize.svg" alt="open full image" />
                </a>

                <img
                  class="item-popup__slider-image"
                  :alt="image.alt"
                  :title="image.title"
                  :src="resolveAssetUrl(image.image_medium || image.image)"
                  itemprop="image"
                />
              </div>
            </template>

            <div v-else class="swiper-slide item-popup-slider__slide">
              <img class="item-popup__slider-image" alt="" :src="require('@/assets/images/icons/nophoto-medium.jpg')" />
            </div>
          </div>

          <template v-if="images.length > 1">
            <div class="swiper-button-prev">
              <i class="icon icon-chevron-left"></i>
            </div>

            <div class="swiper-button-next">
              <i class="icon icon-chevron-right"></i>
            </div>
          </template>
        </div>
      </div>

      <div class="item-popup__content">
        <div class="item-popup__name">
          <h2 class="item-popup__title title-market" itemprop="name">
            {{ getTranslatedProperty(product.product) }} {{ getTranslatedProperty({ ...product.sort }) }}
          </h2>

          <div class="item-popup__meta">
            <p class="item-popup__date">
              {{ localeDate(product.updated_at, true) }}
            </p>

            <div class="item-popup__views">
              <i class="icon-eye" />
              <span>{{ product.num_views }}</span>
            </div>

            <div v-if="isSpecialOffer" class="item-popup__special">
              <svg width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="m2.17178 11.4108-.80902-.4806c-.35825-.2127-.57989-.5967-.585092-1.01333L.766056 8.976C.763553 8.7761.711001 8.58.613201 8.40572L.15293 7.5851c-.2039068-.36349-.2039068-.80672 0-1.1702l.460271-.82062c.0978-.17428.150354-.37038.152855-.57029l.011612-.94086c.005205-.41663.226832-.80061.585092-1.01333l.80902-.4806c.17187-.1021.31533-.24555.41742-.41742l.4806-.80902c.21271-.35825.5967-.57989 1.01333-.585092l.94086-.011612c.19991-.002503.396-.055055.57029-.152855L6.4149.15293c.36349-.2039068.80671-.2039068 1.1702 0l.82062.460271c.17428.0978.37038.150354.57029.152855l.94086.011612c.41663.005205.80063.226832 1.01333.585092l.4806.80902c.1021.17187.2455.31533.4174.41742l.809.4806c.3583.21271.5799.5967.5851 1.01333l.0116.94086c.0025.19991.0551.396.1529.57029l.4603.82062c.2039.36348.2039.80671 0 1.1702l-.4603.82062c-.0978.17428-.1504.37038-.1529.57028l-.0116.94087c-.0052.41663-.2268.80063-.5851 1.01333l-.809.4806c-.1719.1021-.3153.2455-.4174.4174l-.4806.809c-.2127.3583-.5967.5799-1.01333.5851l-.94086.0116c-.19991.0025-.396.0551-.57029.1529l-.82062.4603c-.36349.2039-.80671.2039-1.1702 0l-.82062-.4603c-.17428-.0978-.37038-.1504-.57029-.1529l-.94086-.0116c-.41663-.0052-.80061-.2268-1.01333-.5851l-.4806-.809c-.1021-.1719-.24555-.3153-.41742-.4174Zm2.44809-2.50486c-.21221.25296-.17928.63005.07368.84236.25305.21232.63014.17928.84245-.07368l3.84341-4.58067c.21222-.25295.17929-.63004-.07377-.84235-.25296-.21232-.63005-.17929-.84236.07367L4.61987 8.90594ZM5.7313 5.73163c.46699-.46708.46699-1.22423 0-1.69122-.46709-.46709-1.22424-.46709-1.69132 0-.46699.46699-.46699 1.22416 0 1.69122.46708.46699 1.22423.46699 1.69132 0Zm4.22806 4.22806c.46704-.46699.46704-1.22416 0-1.69122-.46699-.46699-1.22416-.46699-1.69123 0-.46698.46708-.46698 1.22423 0 1.69122.46709.46711 1.22424.46711 1.69123 0Z"
                  fill="#f54c40"
                />
              </svg>

              <p>{{ $t('Special offer') }}</p>
            </div>
          </div>
        </div>
        <div class="item-popup__table-wrapper">
          <table class="item-popup__table table">
            <tbody>
              <tr>
                <td class="item-popup__table-label">
                  {{ $t('Country') }}
                </td>
                <td>{{ getTranslatedProperty({ ...product.country }) || $t('Unknown') }}</td>
              </tr>
              <tr>
                <td class="item-popup__table-label">
                  {{ $t('Quality class') }}
                </td>
                <td>{{ getTranslatedProperty(product.category) }}</td>
              </tr>
              <tr v-if="product.brand">
                <td class="item-popup__table-label">
                  {{ $t('Trademark') }}
                </td>
                <td>{{ product.brand.name }}</td>
              </tr>
              <tr>
                <td class="item-popup__table-label">
                  {{ $t('Dimension') }}
                </td>
                <td>{{ product.caliber }}</td>
              </tr>
              <tr>
                <td class="item-popup__table-label">
                  {{ $t('Kind of packing') }}
                </td>
                <td>{{ getTranslatedProperty(product.packing) }}</td>
              </tr>
              <tr>
                <td class="item-popup__table-label">
                  {{ $t('Packing capacity (kg/liter)') }}
                </td>
                <td>{{ product.quantum }}</td>
              </tr>

              <tr v-if="product.wt_unit.id === unitPcs.id">
                <td class="item-popup__table-label">
                  {{ $t('Weight of one {0}', [$t(`units.weight.${product.wt_unit.name}`)]) }}.
                </td>
                <td>{{ product.one_item_weight }} {{ $t('units.weight.kg') }}</td>
              </tr>

              <tr v-else-if="product.wt_unit.id === unitPkg.id">
                <td class="item-popup__table-label">
                  {{ $t('Weight of one {0}', [$t(`units.weight.${product.wt_unit.name}`)]) }}.
                </td>
                <td>{{ product.one_item_weight }} {{ $t('units.weight.kg') }}</td>
              </tr>

              <template v-if="product.wt_unit.id === unitPkg.id && product.items_in_package > 1">
                <tr>
                  <td class="item-popup__table-label">{{ $t('Amount in pack') }}.</td>
                  <td>{{ product.items_in_package }} {{ $t('units.weight.pcs') }}</td>
                </tr>

                <tr>
                  <td class="item-popup__table-label">{{ $t('Weight of a piece in a package') }}.</td>
                  <td>{{ product.one_item_weight / product.items_in_package }} {{ $t('units.weight.kg') }}</td>
                </tr>
              </template>

              <tr v-if="product.tax">
                <td class="item-popup__table-label">{{ $t('VAT') }}</td>
                <td>{{ $t('Included {0}%', [product.tax]) }}</td>
              </tr>

              <tr class="item-popup__extra-info">
                <td class="item-popup__table-label">
                  {{ $t('Description') }}
                </td>

                <td v-if="product.extra_info" itemprop="description">
                  {{ product.extra_info }}
                </td>

                <td v-else itemprop="description">
                  {{ product.product.name }} {{ product.sort.name }} – {{ product.country.name }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="item-popup__location">
          <span class="item-popup__distributor">
            {{ showDistributor ? $t('Distributor') : $t('Location') }}
          </span>

          <div v-if="showDistributor" class="item-popup__distributor-info" role="button" @click="navigateToDistributor">
            <b-avatar size="sm" :src="product.seller.user.photo_small" :alt="product.seller.user.name" />
            <span>{{ product.seller.user.name }}</span>
          </div>

          <span>
            {{ productLocation.countryName }}, {{ productLocation.cityName
            }}<span v-if="isProvider"> {{ productLocation.address ? `, ${productLocation.address}` : '' }}</span>
          </span>
        </div>

        <div class="item-popup__bottom">
          <div class="item-popup__price-wrapper" itemprop="offers" itemscope itemtype="https://schema.org/Offer">
            <div class="item-popup__price" :class="{ 'item-popup__price--special': isSpecialOffer }">
              <div v-if="hasDiscount" class="discount">
                <span>{{ formatPrice(product.full_price, product.price_unit_text) }}</span>
                <span class="discount">(-{{ product.discount }}%)</span>
              </div>

              <svg
                v-if="product.discount_compensation && isSeller"
                id="sale-icon"
                width="14"
                height="14"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="m2.17178 11.4108-.80902-.4806c-.35825-.2127-.57989-.5967-.585092-1.01333L.766056 8.976C.763553 8.7761.711001 8.58.613201 8.40572L.15293 7.5851c-.2039068-.36349-.2039068-.80672 0-1.1702l.460271-.82062c.0978-.17428.150354-.37038.152855-.57029l.011612-.94086c.005205-.41663.226832-.80061.585092-1.01333l.80902-.4806c.17187-.1021.31533-.24555.41742-.41742l.4806-.80902c.21271-.35825.5967-.57989 1.01333-.585092l.94086-.011612c.19991-.002503.396-.055055.57029-.152855L6.4149.15293c.36349-.2039068.80671-.2039068 1.1702 0l.82062.460271c.17428.0978.37038.150354.57029.152855l.94086.011612c.41663.005205.80063.226832 1.01333.585092l.4806.80902c.1021.17187.2455.31533.4174.41742l.809.4806c.3583.21271.5799.5967.5851 1.01333l.0116.94086c.0025.19991.0551.396.1529.57029l.4603.82062c.2039.36348.2039.80671 0 1.1702l-.4603.82062c-.0978.17428-.1504.37038-.1529.57028l-.0116.94087c-.0052.41663-.2268.80063-.5851 1.01333l-.809.4806c-.1719.1021-.3153.2455-.4174.4174l-.4806.809c-.2127.3583-.5967.5799-1.01333.5851l-.94086.0116c-.19991.0025-.396.0551-.57029.1529l-.82062.4603c-.36349.2039-.80671.2039-1.1702 0l-.82062-.4603c-.17428-.0978-.37038-.1504-.57029-.1529l-.94086-.0116c-.41663-.0052-.80061-.2268-1.01333-.5851l-.4806-.809c-.1021-.1719-.24555-.3153-.41742-.4174Zm2.44809-2.50486c-.21221.25296-.17928.63005.07368.84236.25305.21232.63014.17928.84245-.07368l3.84341-4.58067c.21222-.25295.17929-.63004-.07377-.84235-.25296-.21232-.63005-.17929-.84236.07367L4.61987 8.90594ZM5.7313 5.73163c.46699-.46708.46699-1.22423 0-1.69122-.46709-.46709-1.22424-.46709-1.69132 0-.46699.46699-.46699 1.22416 0 1.69122.46708.46699 1.22423.46699 1.69132 0Zm4.22806 4.22806c.46704-.46699.46704-1.22416 0-1.69122-.46699-.46699-1.22416-.46699-1.69123 0-.46698.46708-.46698 1.22423 0 1.69122.46709.46711 1.22424.46711 1.69123 0Z"
                />
              </svg>

              <b-popover
                hover
                placement="topright"
                triggers="hover click"
                target="sale-icon"
                custom-class="sale-popover"
              >
                {{ $t('The product participates in the promotion at the expense of БAZA') }}
              </b-popover>

              <div>
                <span itemprop="price">
                  {{ formatPrice(product.price, product.price_unit_text, false) }}
                </span>
                <span itemprop="priceCurrency" :content="product.price_unit_text">{{ product.price_unit }}</span>
                <span>/{{ weightUnitText }}</span>
                <link
                  itemprop="availability"
                  :href="
                    isExpired
                      ? 'http://schema.org/OutOfStock'
                      : hasSystemSeller
                      ? 'http://schema.org/InStock'
                      : 'http://schema.org/PreOrder'
                  "
                />
              </div>
            </div>
            <div class="item-popup__num">
              {{ $t('from {0} {1}', [product.weight, weightUnitText]) }}
            </div>
          </div>

          <div v-if="canAddToCart" class="item-popup__button" @click="handleCartButtonClick">
            <span class="item-popup__btn button popup-link">
              {{ isInCart ? $t('In cart') : $t('Add to cart') }}
            </span>
          </div>

          <p v-else-if="isExpired && (isBuyer || !loggedIn)" class="item-popup__unavailable">
            {{ $t('Not available') }}
          </p>

          <button
            v-if="isSeller && product.status === $options.SELLER_PRODUCT_STATUS.publish && !hideActions"
            type="button"
            class="button-reset item-popup__promotion-btn bg-moving-gradient hover-shifting-icons"
            @click="$emit('promotion-click')"
          >
            <div class="item-popup__promotion-btn-icon icons-container">
              <img class="original-icon" src="@/assets/images/icons/megaphone.svg" />
              <img class="replace-icon" src="@/assets/images/icons/megaphone.svg" />
            </div>

            <span>{{ $t('Promotion') }}</span>
          </button>

          <p
            v-if="(isBuyer || !loggedIn) && !isExpired"
            :class="{
              'item-popup__availability': true,
              'item-popup__availability--available': hasSystemSeller,
            }"
          >
            {{ hasSystemSeller ? $t('Available') : $t('Preorder') }}
          </p>
        </div>
      </div>
    </div>

    <footer class="item-popup__footer">
      <UserInfoBox
        small-image
        class="cart__person"
        :name="product.seller.user.name"
        :pic="product.seller.user.photo_small"
        :pic-medium="product.seller.user.photo_medium"
        :show-reviews="false"
        :is-online="product.seller.user.is_online"
        :rating="product.seller.rating"
        :user-route="
          loggedIn
            ? {
                name: 'provider',
                params: { userId: product.seller.id },
              }
            : undefined
        "
        @link-click="
          () => {
            $emit('navigate')
          }
        "
      />
      <UserAdditionalInfoBox
        class="cart__about"
        :show-location="false"
        :show-registration="false"
        :company="{
          name: product.seller.organization.short_title,
          verified: product.seller.organization.verified,
        }"
      />
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { eventBus } from '@/helpers/eventBus'
import localeDate from '@/helpers/filters/localeDate'
import UserInfoBox from '@/components/common/UserInfoBox.vue'
import UserAdditionalInfoBox from '@/components/common/UserAdditionalInfoBox.vue'
import formatPrice from '@/helpers/filters/formatPrice'
import weightString from '@/helpers/filters/weightString'
import { getTranslatedProperty } from '@/helpers/getTranslatedProperty.js'
import { getProductLocation } from '@/helpers/getProductLocation'
import { resolveAssetUrl } from '@/helpers/url'
import { isExpiredProduct } from '@/helpers/getters/isExpiredProduct'
import Swiper, { Navigation } from 'swiper'
import 'swiper/css'
import { SELLER_PRODUCT_STATUS } from '@/constants'
import { ECOMMERCE_ACTION_TYPE, pushEcommerce } from '@/utils/metrics'
import { getAltTextAndTitleForProduct } from '@/helpers/sellerProduct/getAltTextAndTitleForProduct'

export default {
  name: 'ProductModalContent',
  SELLER_PRODUCT_STATUS,
  components: {
    UserInfoBox,
    UserAdditionalInfoBox,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    hideActions: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState('user', ['currentUser']),
    ...mapState('profileProduct', ['sellerProducts']),
    ...mapGetters('cart', ['cartItemsProducts']),
    ...mapGetters('localCart', ['localCartItemsProducts']),
    ...mapGetters('user', ['isSeller', 'isBuyer', 'isSystemSeller', 'isProvider']),
    ...mapGetters('auth', ['loggedIn']),
    ...mapGetters('misc', ['unitPcs', 'unitPkg']),
    hasSystemSeller() {
      return this.product.supplier !== null
    },
    isInCart() {
      if (this.loggedIn) {
        return this.cartItemsProducts.some(item => item && item.seller_product.id === this.product.id)
      }

      return this.localCartItemsProducts.some(p => p.id === this.product.id)
    },
    images() {
      return this.product.images.map(img => ({
        ...img,
        ...getAltTextAndTitleForProduct(this.product),
      }))
    },
    weightUnitText() {
      return this.$t('units.weight.' + this.product.wt_unit.name)
    },
    hasDiscount() {
      return this.product.discount > 0
    },
    isSpecialOffer() {
      return this.product.sort_weight > 0 || this.hasDiscount
    },
    productLocation() {
      return getProductLocation(this.product)
    },
    showDistributor() {
      if (this.isSystemSeller) {
        return false
      }

      if (!this.product.supplier) {
        return false
      }

      return this.product.supplier.id !== this.product.seller.id
    },
    isExpired() {
      return isExpiredProduct(this.product)
    },
    canAddToCart() {
      return (this.isBuyer || !this.loggedIn) && !this.isExpired && !this.hideActions
    },
  },
  mounted() {
    new Swiper(this.$refs.swiper, {
      modules: [Navigation],
      loop: true,
      slidesPerView: 1,
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
      },
    })
  },
  methods: {
    getTranslatedProperty,
    weightString,
    formatPrice,
    handleCartButtonClick() {
      // TODO: Убрать асинхронную магию

      if (this.isInCart) {
        if (this.loggedIn) {
          setTimeout(() => {
            this.$router.push({ name: 'cart' })
          }, 0)
          this.$emit('close')
        } else {
          setTimeout(() => {
            eventBus.$emit('showCartSidebar', true)
          }, 0) // need this to work after modal is destroyed
          this.$emit('close')
        }
        return
      }

      if (this.loggedIn) {
        pushEcommerce({
          actionType: ECOMMERCE_ACTION_TYPE.add,
          products: [this.product],
        })

        this.$emit('close')

        eventBus.$emit('modal', {
          component: 'AddToCart',
          data: this.product,
        })
      } else {
        this.$store.dispatch('localCart/addToLocalCart', this.product)

        pushEcommerce({
          actionType: ECOMMERCE_ACTION_TYPE.add,
          products: [this.product],
        })

        setTimeout(() => {
          eventBus.$emit('showCartSidebar', true)
        }, 0)
        this.$emit('close')
      }

      // if (!this.loggedIn) {
      // this.$emit('navigate')
      // this.$router.push({ name: 'login', query: { redirect: `/market?product=${this.product.id}` } })
      // sendMetrics('click_order_product', {
      //   product_name: this.product.product.name,
      //   product_id: this.product.id,
      // })
      // } else {
      // }
    },
    localeDate,
    resolveAssetUrl,
    navigateToDistributor() {
      this.$emit('navigate')
      this.$router.push({ name: 'provider', params: { userId: this.product.seller.id } })
    },
  },
}
</script>

<style>
.item-popup .swiper-button-prev,
.item-popup .swiper-button-next {
  bottom: 0;
  top: initial;
  cursor: pointer;
}

.item-popup .swiper-button-prev {
  left: 16px;
}

.item-popup .swiper-button-next {
  left: 48px;
  right: initial;
}
</style>

<style lang="scss" scoped>
@import '@/assets/scss/common-imports.scss';
@import '@/assets/scss/silence/scroll-bar-decorative.scss';

.item-popup {
  background-color: $White;

  &__main {
    display: flex;
    flex-direction: column;

    @include media-breakpoint-up(sm) {
      flex-direction: row;
    }
  }

  &__slider-wrap {
    height: 200px;

    @include media-breakpoint-up(sm) {
      width: 50%;
      height: auto;
      max-height: 515px;
    }
  }

  .table {
    td {
      padding: 0 0 8px 0;
      border: none;
    }
  }

  .item-popup__meta {
    display: flex;
    align-items: center;
    margin-right: -$base-grid * 2;
  }

  .item-popup__unavailable {
    color: $LRed;
  }

  .item-popup__availability {
    padding-top: 8px;
    grid-column: 1/-1;
    display: flex;
    justify-content: flex-end;
    font-size: 14px;

    &--available {
      color: $GreenM;
    }
  }

  .item-popup__date {
    font-size: 13px;
    margin-right: 12px;
  }

  .item-popup__views {
    display: flex;
    align-items: center;

    i {
      font-size: 14px;
    }

    span {
      padding-left: 4px;
    }
  }

  .item-popup__special {
    display: flex;
    align-items: center;
    color: $DGray2;
    margin-left: auto;
    font-size: 14px;
    padding: 3px 8px;
    border-radius: 4px;
    border: 1px solid $LGray3;

    p {
      padding-left: 4px;
    }
  }

  &__slider {
    position: relative;
    width: 100%;
    height: 100%;
  }

  &__slider-item {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__slider-image {
    width: 100%;
    height: 100%;
    object-fit: cover;

    @include media-breakpoint-down(sm) {
      object-fit: contain;
    }
  }

  &__slider-maximize {
    position: absolute;
    right: 10px;
    bottom: 10px;
    img {
      width: 25px;
      opacity: 0.7;
    }

    &:hover img {
      opacity: 1;
    }

    @include media-breakpoint-up(sm) {
      top: 12px;
      bottom: unset;
    }
  }

  &__no-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .item-popup__content {
    display: flex;
    flex-direction: column;
    background-color: $White;
    color: $Black;
    border-radius: 0 3px 0 0;
    padding: 24px;

    @include media-breakpoint-down(sm) {
      padding: 16px 14px;
    }

    @include media-breakpoint-up(sm) {
      flex: 0 1 50%;
    }
  }

  .item-popup__name {
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    padding-right: $base-grid * 2.5;
    margin-bottom: 12px;
  }

  .item-popup__title {
    margin-bottom: 4px;
    font-size: 22px;
    color: $Black;

    @include media-breakpoint-up(sm) {
      margin-bottom: 12px;
    }
  }

  .applications__view {
    justify-content: flex-start;

    i {
      margin-right: 7px;
      font-size: 10px;
    }
  }

  .item-popup__table-wrapper {
    @extend %scroll-bar-decorative;
    margin-bottom: 16px;

    @include media-breakpoint-down(xs) {
      margin-right: -6px;
      padding-right: 6px;
      max-height: 120px;
      overflow-y: scroll;
    }
  }

  .item-popup__table {
    margin-bottom: 0;
    tr {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      font-size: 14px;
      line-height: 16px;

      &:last-child {
        td {
          padding-bottom: 0;
        }
      }
    }

    td {
      padding: 0 0 8px 0;
      border: none;
    }
  }

  .item-popup__table-label {
    display: inline-block;
    margin-right: $base-grid * 2;
  }

  .item-popup__extra-info {
    width: 100%;

    td {
      word-break: break-word;
    }

    .item-popup__table-label {
      flex: 0 1 100%;
      margin: 0 0 $base-grid * 0.5;
    }
  }

  .item-popup__location {
    font-weight: 500;
    border: 1px solid $LGray6;
    padding: 8px;
    border-radius: 8px;
    color: $Dark100;
    margin-bottom: 16px;
  }

  .item-popup__distributor {
    display: block;
    font-weight: 500;
    font-size: 13px;
    color: $DGray;
    margin-bottom: 4px;
    line-height: 1;
  }

  .item-popup__distributor-info {
    padding-bottom: 8px;
    border-bottom: 1px solid $LGray3;
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
  }

  .item-popup__bottom {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    margin-top: auto;
  }

  .item-popup__price-wrapper {
    flex: 1 1 auto;
    font-weight: 600;
    font-size: 18px;
    line-height: 23px;

    @include media-breakpoint-down(lg) {
      font-size: calc(14px + 4 * ((100vw - #{$layoutMobile}px) / (#{$layoutDesktop} - #{$layoutMobile})));
      line-height: calc(23 / 18);
    }
  }

  .item-popup__price {
    font-weight: 800;
    font-size: 18px;
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;

    &--special {
      color: $BrightRed;

      .discount {
        flex: 0 1 100%;
        font-weight: 500;
        font-size: 15px;

        > span:first-child {
          color: $DGray;
          text-decoration: line-through;
        }

        * + * {
          margin-left: 8px;
        }
      }

      > svg {
        margin-right: 4px;
      }
    }
  }

  .item-popup__num {
    font-weight: 500;
    color: $DGray;
  }

  .item-popup__button {
    min-width: 120px;
  }

  .item-popup__btn {
    padding: 10px 12px;

    &:hover {
      color: $White;
    }

    &:focus {
      box-shadow: none;
    }

    @include media-breakpoint-up(sm) {
      padding: 12px;
    }
  }

  .item-popup__footer {
    position: relative;
    margin: 0;
    padding: 20px 15px;
    color: $White;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;

    &::before {
      content: '';
      width: calc(100% - 30px);
      height: 1px;
      position: absolute;
      top: 0;
      left: 15px;
      background-color: $LGray3;
    }
  }

  .item-popup__promotion-btn {
    display: flex;
    align-items: center;
    justify-self: flex-start;
    gap: 4px;
    background-color: $Indigo;
    border-radius: 8px;
    color: $White;
    padding: 8px;
    position: relative;
    overflow: hidden;

    &-icon {
      width: 20px;
      height: 20px;
    }
  }
}

.sale-popover {
  z-index: 10100;
}
</style>
